import React, { useState, useEffect } from 'react';
import { Grid, Button, Typography, Select, MenuItem, FormLabel, FormControl, Box, Input, Icon, ListItemIcon, ListItemText } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FilterIcon from '@mui/icons-material/FilterList';
import { useSelector, useDispatch } from 'react-redux';
import { get, set, size } from 'global/lib/lodash';
import setFilter from 'redux/actions/setFilter';
import { TaskFilter } from 'global/utils/filter';
import { TaskStatus } from 'global/lib/filter';
import { useScreen } from 'global/styles';
import useStyles from 'global/styles/filters';
import DiagContainer from 'global/components/diag-container';
import { setBodyOverflow } from 'global/hooks/useBodyOverflow';
import { OwnershipTask, TaskContent } from 'global/lib/filter';
import { sortAlphaNum } from 'global/utils';
import { FilterChips } from 'global/components/filterchips';
import clsx from 'clsx';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useFilter } from '../../../global/hooks/useFilter';
import { ensureArray } from '../../../global/utils';
import { useTradeTypes } from '../../../global/components/trade-select';
import { useTaskTypes } from '../task-type-utils/useTaskTypes';
import { getAvailableTaskTypes, getTaskTypeIcon } from '../task-type-utils';

export default function PerformTasksFilters({ display, vendors }) {
  const [showFilters, setShowFilters] = useState(false);
  const filter = useSelector((state) => get(state, `filter.tasks.${display}`, TaskFilter()));
  const dispatch = useDispatch();
  const fhook = useFilter();
  const { ownership, status, content, scorecard_id, trade_types, task_type_ids } = filter;

  const prefixToUse = 'vendor';

  const { isMobile } = useScreen();
  const useModalFilters = isMobile;

  const classes = useStyles({ isMobile: useModalFilters || isMobile });

  const showContentToggle = display === 'scorecard';
  const isSC = display === 'scorecard';

  const scorecards = useRetrieveData(
    { url: '/scorecards' },
    {
      build: ['scorecards'],
      useCache: { reload: 20, store: 'data.scorecards' },
      params: {
        include: 'realms',
      },
    }
  );

  const trades = useTradeTypes();
  const { data: taskTypesData } = useTaskTypes();
  const availableTaskTypes = getAvailableTaskTypes(taskTypesData);

  useEffect(() => {
    setShowFilters(!useModalFilters);
  }, [useModalFilters]);

  const changeFilter = (filt) => {
    dispatch(setFilter('tasks', display, filt));
    setBodyOverflow();
  };

  const handleStatusChange = (e) => {
    changeFilter(set({ ...filter }, `status`, e.target.value));
  };

  const handleScorecardChange = (e) => {
    const hasAny = e.target.value.find((v) => v === '0') || !size(e.target.value);
    changeFilter(set({ ...filter }, `scorecard_id`, hasAny ? [0] : e.target.value.filter((f) => !!f)));
  };

  const handleTradeChange = (e) => {
    const hasAny = e.target.value.find((v) => v === '0') || !size(e.target.value);
    changeFilter(set({ ...filter }, `trade_types`, hasAny ? [0] : e.target.value.filter((f) => !!f)));
  };

  const handleTaskTypeChange = (e) => {
    const hasAny = e.target.value.find((v) => v === '0') || !size(e.target.value);
    changeFilter(set({ ...filter }, `task_type_ids`, hasAny ? [0] : e.target.value.filter((f) => !!f)));
  };

  const handleOwnership = (e, val) => {
    if (val !== null) {
      changeFilter(set({ ...filter }, `ownership`, val));
    }
  };

  const handleContent = (e, val) => {
    if (val !== null) {
      changeFilter(set({ ...filter }, `content`, val));
    }
  };

  const handleVendorChange = (e) => {
    const vend = get(vendors, 'data', []).find((v) => v.id == e.target.value);
    changeFilter(set({ ...filter }, `${prefixToUse}_id`, get(vend, `${prefixToUse}Id`, null)));
  };

  function toggleFilters() {
    setShowFilters(!showFilters);
  }

  const hasVendors = size(get(vendors, 'data')) > 0;
  const client = get(vendors, 'data', []).find((v) => get(v, [prefixToUse, 'id']) == get(filter, `${prefixToUse}_id`));
  const scorecards_data = fhook.filterScorecards(scorecards.data);

  return (
    <Box displayPrint="none">
      {useModalFilters && (
        <div className={classes.filterRow}>
          <Button className="FilterBtn" aria-label="filters" size="small" onClick={toggleFilters}>
            <FilterIcon fontSize="inherit" className={classes.icon} /> {showFilters ? 'Hide Filters' : 'Filters'}
          </Button>
          <FilterChips id={`tasks.${display}`} />
        </div>
      )}
      <DiagContainer useDialog={useModalFilters} onClose={toggleFilters} open={showFilters}>
        <Typography variant="overline">Filters</Typography>

        <form autoComplete="off" margin="normal" className={classes.form}>
          <Grid container alignContent="center">
            <Grid item xs={12}>
              <div className={classes.flexFilters}>
                <ToggleButtonGroup exclusive className={classes.btnGroup} size="small" value={ownership} onChange={handleOwnership}>
                  {OwnershipTask.map((t) => (
                    <ToggleButton key={t.value} value={t.value}>
                      {t.title}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {/* <ToggleButtonGroup className={classes.btnGroup} size="small" value={realm_type} onChange={handleRealmType}>
                  <ToggleButton title="Lot" value='lot'><HomeIcon /></ToggleButton>
                  <ToggleButton title="Community" value='community'><CommunityIcon /></ToggleButton>
                  <ToggleButton title="Division" value='division'><DivisionIcon /></ToggleButton>
                </ToggleButtonGroup> */}

                {showContentToggle && (
                  <div className={classes.smallMarginTop}>
                    <ToggleButtonGroup exclusive className={classes.btnGroup} size="small" value={content} onChange={handleContent}>
                      {TaskContent.map((t) => (
                        <ToggleButton key={t.value} value={t.value}>
                          {t.title}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </div>
                )}
              </div>
            </Grid>

            <Grid item xs={12} className={classes.formGrid}>
              <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                  Status
                </FormLabel>
                <Select value={status} onChange={handleStatusChange}>
                  {TaskStatus.map((ts, i) => (
                    <MenuItem key={i} value={ts.value}>
                      {ts.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {isSC && (
              <Grid item xs={12} className={classes.formGrid}>
                <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                  <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                    Trade Type
                  </FormLabel>
                  <Select multiple value={ensureArray(trade_types)} onChange={handleTradeChange}>
                    <MenuItem value={'0'}>
                      <em>Any</em>
                    </MenuItem>
                    {trades.data.map((ts, i) => (
                      <MenuItem key={i} value={ts.id}>
                        {ts._name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {isSC && size(availableTaskTypes) > 0 && (
              <Grid item xs={12} className={classes.formGrid}>
                <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                  <FormLabel className={classes.formLabel}>
                    Task Types
                  </FormLabel>
                  <Select
                    multiple
                    value={ensureArray(task_type_ids)}
                    onChange={handleTaskTypeChange}
                    renderValue={(selected) => {
                      if (!selected.length || (selected.length === 1 && selected[0] == '0')) return 'Any';
                      return selected.map((id) => {
                        const tt = availableTaskTypes.find((t) => String(t.id) === id);
                        return tt ? tt.name : id;
                      }).join(', ');
                    }}
                  >
                    <MenuItem value={'0'}>
                      <em>Any</em>
                    </MenuItem>
                    {availableTaskTypes.map((tt, i) => (
                      <MenuItem key={tt.id} value={String(tt.id)}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Icon fontSize="small">{getTaskTypeIcon(tt)}</Icon>
                        </ListItemIcon>
                        <ListItemText>{tt.name}</ListItemText>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {isSC && (
              <Grid item xs={12} className={classes.formGrid}>
                <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                  <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                    Scorecard
                  </FormLabel>
                  <Select multiple value={ensureArray(scorecard_id)} onChange={handleScorecardChange} input={<Input name="scorecard" id="Standard-scorecard-simple" />}>
                    <MenuItem value={'0'}>
                      <em>Any</em>
                    </MenuItem>
                    <MenuItem value={'none'}>
                      <em>None</em>
                    </MenuItem>
                    {size(scorecards_data) &&
                      scorecards_data.map((card, i) => {
                        return (
                          <MenuItem key={`${card.id}-${i}`} value={card.id}>
                            {card.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {hasVendors && (
              <Grid item xs={12} className={classes.formGrid}>
                <FormControl variant="standard" className={clsx(classes.formItem, classes.formControlPadding)}>
                  <FormLabel htmlFor="Standard-scorecard-simple" className={classes.formLabel}>
                    Company
                  </FormLabel>
                  <Select value={get(client, 'id', 0)} onChange={handleVendorChange}>
                    <MenuItem value={0}>
                      <em>Any</em>
                    </MenuItem>
                    {get(vendors, 'data', [])
                      .sort((a, b) => sortAlphaNum(a, b, 'vendor.name'))
                      .map((vend, i) => (
                        <MenuItem key={vend.id} value={vend.id}>
                          {get(vend, [prefixToUse, 'name'], '')}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </form>
      </DiagContainer>
    </Box>
  );
}
